import clsx from 'clsx'
import React, {PropsWithChildren} from 'react'

import {BaseProps, Size} from '../types'
import {forwardOverridableRef} from '../util'
import {ClassKey, useClasses} from './Grid.classes'
import type {
  GridContentAlignment,
  GridDirection,
  GridItemsAlignment,
  GridJustification,
  GridSizeProp,
  GridSpacing,
  GridWrap,
} from './Grid.types'

export type GridTypeMap = {
  props: PropsWithChildren<{
    alignContent?: GridContentAlignment
    alignItems?: GridItemsAlignment
    container?: boolean
    direction?: GridDirection
    item?: boolean
    justify?: GridJustification
    xs?: GridSizeProp
    sm?: GridSizeProp
    md?: GridSizeProp
    lg?: GridSizeProp
    xl?: GridSizeProp
    /**
     * @deprecated use size instead, xs=4; s=8 (spacing=1); m=16 (spacing=2); l=24 (spacing=3); xl=32 (spacing=4)
     */
    spacing?: GridSpacing
    size?: Size
    wrap?: GridWrap
    zeroMinWidth?: boolean
    onPress?: VoidFunction
  }>
  defaultComponent: 'div'
  classKey: ClassKey
}

/**
 * Пропсы грида. Допускают только те пропсы, которые гарантированно может принять грид.
 */
export type GridProps = BaseProps<GridTypeMap>

const Grid = forwardOverridableRef<GridTypeMap>((props, ref) => {
  const {
    alignContent = 'stretch',
    alignItems = 'stretch',
    classes: classesProp,
    className: classNameProp,
    component: Component = 'div',
    container = false,
    direction = 'row',
    item = false,
    justify = 'flex-start',
    wrap = 'wrap',
    zeroMinWidth = false,
    spacing,
    size,
    xs,
    sm,
    md,
    lg,
    xl,
    onClick,
    onPress,
    ...other
  } = props
  const classes = useClasses(classesProp)

  const className =
    clsx(
      classes.root,
      container && classes.container,
      item && classes.item,
      zeroMinWidth && classes.zeroMinWidth,
      container && spacing && classes[`spacing_${spacing}`],
      direction !== 'row' && classes[`direction_${direction}`],
      wrap !== 'wrap' && classes[`wrap_${wrap}`],
      alignItems !== 'stretch' && classes[`alignItems_${alignItems}`],
      alignContent !== 'stretch' && classes[`alignContent_${alignContent}`],
      justify !== 'flex-start' && classes[`justifyContent_${justify}`],
      xs && classes[`grid_xs_${xs}`],
      sm && classes[`grid_sm_${sm}`],
      md && classes[`grid_md_${md}`],
      lg && classes[`grid_lg_${lg}`],
      xl && classes[`grid_xl_${xl}`],
      size && classes[`size_${size}`],
      classNameProp,
    ) || undefined // чтобы пустая строка не прилетала в пропс

  return <Component ref={ref} className={className} onPointerUp={onPress || onClick} {...other} />
})

// @ts-expect-error типизация типизацией, а в девтулзах имя нужно
Grid.displayName = 'Grid'

export default Grid
